import { useEffect } from 'react';
import Footer from '@/pages/home/components/Footer';

export default function ShoppingGuide() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'Руководство по покупкам: как заказывать товары из Китая через YOYBUY Spreadsheet',
    description: 'Пошаговое руководство по заказу товаров из Китая с помощью YOYBUY Spreadsheet. От поиска товаров до получения посылки.',
    step: [
      { '@type': 'HowToStep', position: 1, name: 'Выберите товары', text: 'Найдите нужные товары на 1688, Taobao или Weidian. Скопируйте ссылки.' },
      { '@type': 'HowToStep', position: 2, name: 'Заполните таблицу', text: 'Вставьте скопированные ссылки в ваш YOYBUY Spreadsheet.' },
      { '@type': 'HowToStep', position: 3, name: 'Подтвердите заказ', text: 'Агент проверит каждую позицию у поставщика и уточнит наличие и цены.' },
      { '@type': 'HowToStep', position: 4, name: 'Контроль качества', text: 'Все товары проходят проверку на складе агента в Китае.' },
      { '@type': 'HowToStep', position: 5, name: 'Консолидация', text: 'Агент объединяет все подтверждённые товары в одну посылку.' },
      { '@type': 'HowToStep', position: 6, name: 'Доставка', text: 'Выберите удобный способ доставки и отслеживайте посылку.' },
    ],
  };

  const steps = [
    {
      num: '01',
      title: 'Выберите товары',
      desc: 'Найдите нужные товары на 1688, Taobao или Weidian. Скопируйте ссылки на страницы товаров, которые вас интересуют. Обратите внимание на рейтинг продавца, количество заказов и отзывы других покупателей.',
    },
    {
      num: '02',
      title: 'Заполните таблицу',
      desc: 'Вставьте скопированные ссылки в ваш YOYBUY Spreadsheet. Для каждой позиции укажите размер, цвет, количество и особые пожелания. Таблица автоматически структурирует все данные для агента.',
    },
    {
      num: '03',
      title: 'Подтвердите заказ',
      desc: 'Ваш персональный агент проверит каждую позицию у поставщика, уточнит наличие и актуальные цены. Вы получите сводку и сможете подтвердить или скорректировать заказ.',
    },
    {
      num: '04',
      title: 'Контроль качества',
      desc: 'Все товары проходят проверку на складе агента в Китае. Агент фотографирует каждый товар и отправляет вам фото. Вы подтверждаете качество перед отправкой.',
    },
    {
      num: '05',
      title: 'Консолидация',
      desc: 'Агент объединяет все подтверждённые товары в одну посылку. Это позволяет значительно снизить стоимость международной доставки по сравнению с отправкой каждого товара отдельно.',
    },
    {
      num: '06',
      title: 'Доставка',
      desc: 'Выберите удобный способ доставки: экспресс (5–10 дней), авиапочта (10–20 дней) или море (25–45 дней). Получите трекинг-номер и отслеживайте посылку.',
    },
  ];

  return (
    <main className="bg-background-50 min-h-screen">
      {/* Navbar */}
      <nav className="bg-background-50 sticky top-0 z-40 border-b border-background-200/50">
        <div className="max-w-4xl mx-auto px-6 flex items-center justify-between h-16">
          <a href="/" className="flex items-center gap-2.5 no-underline">
            <div className="w-8 h-8 rounded-full bg-accent-500 flex items-center justify-center">
              <span className="text-background-50 font-bold text-sm font-heading">Y</span>
            </div>
            <span className="font-bold text-foreground-950 text-sm whitespace-nowrap font-sans">yoybuy spreadsheet</span>
          </a>
          <a href="/" className="text-foreground-500 hover:text-foreground-950 text-sm font-medium font-sans no-underline transition-colors whitespace-nowrap">
            <i className="ri-arrow-left-line w-4 h-4 inline-flex items-center justify-center mr-1"></i>
            На главную
          </a>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-6 py-12 md:py-20">
        {/* Header */}
        <header className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium font-sans whitespace-nowrap">Руководство</span>
            <span className="text-foreground-400 text-sm font-sans">Обновлено: Июль 2026</span>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-950 font-bold leading-tight mb-6">
            Руководство по покупкам: как заказывать товары из Китая через YOYBUY Spreadsheet
          </h1>
          <div className="w-full h-64 md:h-80 rounded-3xl overflow-hidden mb-8">
            <img
              src="https://readdy.ai/api/search-image?query=Clean%20minimalist%20workspace%20scene%20with%20fashion%20items%20neatly%20arranged%20around%20laptop%20and%20notebook%20on%20cream%20background%2C%20Scandinavian%20editorial%20style%2C%20warm%20neutral%20tones%2C%20soft%20natural%20lighting%2C%20organized%20creative%20workspace%2C%20product%20curation%20concept%2C%20high%20detail%2C%20elegant%20composition%2C%20no%20people&width=1200&height=600&seq=yoybuy-shopping-guide-hero&orientation=landscape"
              alt="Руководство по покупкам"
              className="w-full h-full object-cover"
            />
          </div>
          <p className="text-foreground-500 text-lg leading-relaxed font-sans">
            Это подробное руководство проведёт вас через весь процесс заказа товаров из Китая с помощью YOYBUY Spreadsheet. От поиска товаров на китайских площадках до получения посылки — каждый шаг описан максимально подробно, чтобы даже новичок мог легко начать.
          </p>
        </header>

        {/* Steps */}
        <section className="mb-12">
          <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 font-semibold mb-8">
            Пошаговый процесс заказа
          </h2>
          <div className="space-y-8">
            {steps.map((step, i) => (
              <div key={i} className="flex gap-5">
                <div className="w-14 h-14 rounded-2xl bg-foreground-950 flex items-center justify-center flex-shrink-0">
                  <span className="text-background-50 font-bold text-lg font-heading">{step.num}</span>
                </div>
                <div>
                  <h3 className="font-sans text-foreground-950 font-semibold text-xl mb-2">{step.title}</h3>
                  <p className="text-foreground-500 leading-relaxed font-sans">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Tips */}
        <section className="mb-12 bg-background-100 rounded-3xl p-8 md:p-10">
          <h2 className="font-heading text-2xl text-foreground-950 font-semibold mb-6">Полезные советы</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { title: 'Проверяйте рейтинг продавца', desc: 'На 1688 и Taobao обращайте внимание на количество успешных сделок и отзывы. Агент YOYBUY также проверит продавца.' },
              { title: 'Заказывайте образцы', desc: 'Перед крупным заказом рекомендуем заказать один образец для проверки качества товара.' },
              { title: 'Консолидируйте заказы', desc: 'Объединяйте несколько товаров в одну посылку — это снижает стоимость доставки на 30–50%.' },
              { title: 'Используйте страхование', desc: 'Для дорогих заказов рекомендуем оформлять страховку посылки — это стоит недорого, но даёт спокойствие.' },
            ].map((tip) => (
              <div key={tip.title} className="flex gap-3">
                <i className="ri-lightbulb-line text-accent-500 text-xl w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                <div>
                  <h4 className="font-sans text-foreground-950 font-semibold text-sm mb-1">{tip.title}</h4>
                  <p className="text-foreground-500 text-sm leading-relaxed font-sans">{tip.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-foreground-950 rounded-3xl p-8 md:p-12 text-center mb-12">
          <h2 className="font-heading text-2xl md:text-3xl text-background-50 font-semibold mb-4">
            Готовы сделать первый заказ?
          </h2>
          <p className="text-foreground-300 mb-8 max-w-lg mx-auto font-sans">
            Следуйте этому руководству и начните экономить на покупках из Китая уже сегодня
          </p>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-3 px-10 py-4 bg-accent-500 text-background-50 rounded-full font-semibold text-base hover:bg-accent-600 transition-colors no-underline whitespace-nowrap cursor-pointer"
          >
            Перейти к YOYBUY Spreadsheet
            <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
          </a>
        </section>

        {/* Internal Links */}
        <nav className="border-t border-background-200/60 pt-8 mb-12">
          <h3 className="font-sans text-foreground-950 font-semibold text-lg mb-4">Полезные ссылки</h3>
          <div className="flex flex-wrap gap-3">
            <a href="/" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 text-foreground-700 hover:text-foreground-950 text-sm font-medium font-sans no-underline transition-colors whitespace-nowrap">
              <i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>
              Главная YOYBUY Spreadsheet
            </a>
            <a href="/articles/yoybuy-spreadsheet-guide" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 text-foreground-700 hover:text-foreground-950 text-sm font-medium font-sans no-underline transition-colors whitespace-nowrap">
              <i className="ri-article-line w-4 h-4 flex items-center justify-center"></i>
              Полное руководство
            </a>
          </div>
        </nav>
      </article>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Footer />
    </main>
  );
}